import React from "react";
import {HostedInstanceResource} from "../../../client/resources/hostedInstanceResource";
import {BusyState} from "../../../components/BusyFromPromise/BusyFromPromise";
import {DataBaseComponent, DataBaseComponentState, Errors} from "../../../components/DataBaseComponent";
import {HostedInstanceRetrieveSqlDeadlockGraphsRequest} from "../../../client/resources/hostedInstanceRetrieveSqlDeadlockGraphsRequest";
import SaveDialogLayout from "../../../components/DialogLayout/SaveDialogLayout";
import repository from "../../../client/repository";
import moment from "moment";
import {createStaticRanges} from "react-date-range";
import DateRangePicker, {DateRange} from "components/DateRangePicker";
import { Callout } from "@octopusdeploy/design-system-components";
import InternalLink from "~/components/Navigation/InternalLink";
import {instancesRouteLinks} from "../instancesRouteLinks";
import InputLabel from "~/components/form/InputLabel";

interface RetrieveDeadlockGraphsDialogProps {
    errors: Errors;
    busy: BusyState;
    instance: HostedInstanceResource;
    onSaveClick(): Promise<boolean>;
}

interface RetrieveSqlDeadlockGraphsDialogState extends DataBaseComponentState {
    range: DateRange;
}

export class RetrieveSqlDeadlockGraphsDialog extends DataBaseComponent<RetrieveDeadlockGraphsDialogProps, RetrieveSqlDeadlockGraphsDialogState> {
    constructor(props: RetrieveDeadlockGraphsDialogProps) {
        super(props);

        this.state = {
            range: {
                startDate: moment().startOf("day"),
                endDate: moment().endOf("day"),
            }
        }
    }

    render() {
        const startOfToday = moment().startOf("day");
        const endOfToday = moment().endOf("day");
        const staticRanges = createStaticRanges([
            {label: "Today", range: () => ({startDate: startOfToday.toDate(), endDate: endOfToday.toDate()})},
            {label: "Yesterday", range: () => ({startDate: startOfToday.clone().subtract(1, "days").toDate(), endDate: endOfToday.clone().subtract(1, "days").toDate()})},
            {label: "Last 7 days", range: () => ({startDate: startOfToday.clone().subtract(7, "days").toDate(), endDate: endOfToday.toDate()})},
            {label: "Last 30 days", range: () => ({startDate: startOfToday.clone().subtract(30, "days").toDate(), endDate: endOfToday.toDate()})},
        ]);
        const minDate = startOfToday.clone().subtract(30, "days");
        const maxDate = endOfToday;

        return <SaveDialogLayout title={"Retrieve SQL Deadlock Graphs"}
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 saveButtonLabel={"Retrieve"}
                                 cancelButtonLabel={"Cancel"}
                                 onSaveClick={() => this.retrieveDeadlockGraphs()}>

            <Callout type="warning" title="Please note">
                Due to database table names previously containing angled brackets (&lt;, &gt;), deadlock graphs generated prior to October 23, 2025 may not be retrievable.
                If you need access to older deadlock graphs, please contact <strong>#cloud-platform-requests</strong> for assistance.
            </Callout>

            <br />

            <InputLabel label="Date range" />

            <DateRangePicker
                startDate={this.state.range.startDate}
                endDate={this.state.range.endDate}
                onChange={range => this.setState({range})}
                staticRanges={staticRanges}
                months={1}
                minDate={minDate.toDate()}
                maxDate={maxDate.toDate()}
            />

            <br/>

            <Callout type="information" title="Results">
                A task will be created in the background to retrieve SQL deadlock graphs.<br/>
                Once the <InternalLink to={instancesRouteLinks.instance(this.props.instance.Id).tasks}>task</InternalLink> completes,
                the deadlock graph files can be downloaded from the task itself or the Instance's <InternalLink to={instancesRouteLinks.instance(this.props.instance.Id).diagnosticFiles}>Diagnostic Files</InternalLink> page.
            </Callout>
        </SaveDialogLayout>;
    }

    private retrieveDeadlockGraphs() {
        const request = {
            from: this.state.range.startDate.toISOString(),
            to: this.state.range.endDate.toISOString()
        };
        
        return this.doBusyTask(async () => {
            await repository.HostedInstances.retrieveSqlDeadlockGraphs(this.props.instance, request as HostedInstanceRetrieveSqlDeadlockGraphsRequest);
            await this.props.onSaveClick();
            return true;
        })
    }
}
